// The picture we draw ourselves for an object that has parts.
//
// Most objects in a library have no flow of their own: what they have is a
// picture somebody exported years ago - a column of flat grey boxes with the
// captions sitting on top of each other - and a list of parts in the graph.
// The export can be cleaned up (services/svgEnhance.js) but never rearranged,
// because rearranging it would mean moving things the author placed. The
// graph, on the other hand, we hold in full: the parts, which relationship
// each was reached by, which way it points, their order, and the colour the
// view configuration gives each type. That is enough to *draw* the object,
// rather than show somebody's picture of it.
//
// This module is the drawing's geometry and nothing else: given the object,
// its parts and the width on screen, it answers where every box, every
// caption and every connector goes. It is pure - no React, no DOM, no
// measurement of a rendered page - so the algorithm can be read and tested on
// its own, and so two readers on two screens of the same width always get the
// same picture.
//
// Nothing here knows a type name, a relationship name or a customer's
// convention. Groups come from the relationships the server already grouped
// the parts by; colours come from the view configuration; the order is the
// order the server sent (the flow's, where the object has one).
//
// ── One layout, two renderers ───────────────────────────────────────────────
// This module is written as plain CommonJS and has NO imports, because two
// programs read it: the browser canvas (`GeneratedCanvas.jsx`) and the
// server's SVG writer (`backend/services/generatedMap.js`, through
// `backend/services/generatedLayout.js`). The same input gives both the same
// boxes, so the picture a reader sees, the file they download and the one
// `/api/v1/.../render` draws are one picture.
//
// It lives here - inside the frontend's own source tree - because Create
// React App refuses an import from outside `src/`, while Node can require any
// path. The backend finds it through its own one-line resolver, and the
// Dockerfile copies this file into the server image as `shared/`. Move it and
// both of those have to move with it.

// ---------------------------------------------------------------------------
// Sizes. One place, so the picture can be re-proportioned without hunting.

const METRICS = Object.freeze({
  margin: 24,
  // A card wide enough for three or four words a line at 13px, narrow enough
  // that a phone still gets a whole one and a laptop gets five or six.
  cardWidth: 208,
  cardPadding: 12,
  // The gaps are where the connectors run, so they are not decoration: a
  // horizontal arrow needs about 28px to read as an arrow.
  gapX: 28,
  gapY: 26,
  bandGap: 34,
  headingHeight: 26,
  titleFontSize: 13,
  titleLineHeight: 16,
  titleLines: 3,
  typeFontSize: 10,
  typeLineHeight: 14,
  footerHeight: 16,
  objectFontSize: 17,
  objectLineHeight: 22,
  objectLines: 2,
  objectMaxWidth: 640,
  railInset: 14,
  maxColumns: 6,
  // Room kept clear at the foot of the drawing for the line that says how
  // much of the object was drawn, so it never sits on a card.
  legendSpace: 30,
});

// How many parts a drawn map shows before it says how many it left out. The
// admin can change it (`diagram.generatedMaxParts`); this is what it is
// without anyone touching anything.
const DEFAULT_CAP = 48;

// ---------------------------------------------------------------------------
// Measuring text without a browser
//
// The picture is laid out on the server's terms - a pure function, run before
// anything is on screen - so there is nothing to measure against. These are
// the proportions of a humanist sans at the weights the cards use: narrow
// letters, wide letters, capitals and the rest, as a fraction of the font
// size. They only decide where a line wraps, so being a few percent out costs
// a word moving to the next line, never an overlap: the card's width is fixed
// and the height follows the number of lines.
const NARROW = new Set("ijlftIr.,;:'`|!()[]{}-".split(""));
const WIDE = new Set("mwMW@%".split(""));

function textWidth(text, fontSize) {
  let units = 0;
  const value = String(text || "");
  for (let i = 0; i < value.length; i += 1) {
    const c = value[i];
    if (c === " ") units += 0.28;
    else if (NARROW.has(c)) units += 0.32;
    else if (WIDE.has(c)) units += 0.9;
    else if (c >= "A" && c <= "Z") units += 0.67;
    else if (c >= "0" && c <= "9") units += 0.56;
    else units += 0.54;
  }
  return units * fontSize;
}

// Break a name into at most `maxLines` lines that each fit `width`. A word too
// long for a line on its own is cut rather than allowed to run out of the
// card; the last line ends in an ellipsis where anything was left out, so a
// shortened name never pretends to be the whole one.
function wrapText(text, width, fontSize, maxLines) {
  const words = String(text || "").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];
  const lines = [];
  let line = "";
  const fits = (candidate) => textWidth(candidate, fontSize) <= width;
  const cut = (word) => {
    let kept = "";
    for (const c of word) {
      if (!fits(`${kept}${c}…`)) break;
      kept += c;
    }
    return kept ? `${kept}…` : "…";
  };
  words.forEach((word) => {
    if (lines.length >= maxLines) return;
    const candidate = line ? `${line} ${word}` : word;
    if (fits(candidate)) {
      line = candidate;
      return;
    }
    if (line) lines.push(line);
    if (lines.length >= maxLines) {
      line = "";
      return;
    }
    line = fits(word) ? word : cut(word);
    if (line.endsWith("…")) {
      lines.push(line);
      line = "";
    }
  });
  if (line && lines.length < maxLines) lines.push(line);
  // Anything that did not fit is admitted to, on the last line.
  const used = lines.join(" ").replace(/…$/, "");
  const all = words.join(" ");
  if (used.length < all.length && lines.length > 0 && !lines[lines.length - 1].endsWith("…")) {
    const last = lines[lines.length - 1];
    let shortened = last;
    while (shortened && !fits(`${shortened}…`)) shortened = shortened.slice(0, -1);
    lines[lines.length - 1] = `${shortened}…`;
  }
  return lines;
}

// ---------------------------------------------------------------------------
// Grouping and the cap

// One band per relationship and direction, in the order the relationships
// first appear, with the parts keeping the order the server sent.
//
// The server groups a map into runs - consecutive parts that share a
// relationship - which is right for a list. In a drawing it is not: once the
// parts are in the order of the object's own flow, one relationship breaks
// into five or six runs of one or two parts each, and the picture becomes a
// tall thin ladder of headings. Merging by relationship gives one section per
// relationship, wide enough to use the screen, and the flow's order still
// runs through it.
function bandsOf(parts) {
  const bands = [];
  const byKey = new Map();
  (parts || []).forEach((part, index) => {
    if (!part) return;
    const relationship = part._relationship || "";
    const direction = part._direction || "";
    const key = `${relationship}|${direction}`;
    let band = byKey.get(key);
    if (!band) {
      band = {
        relationship,
        direction,
        label: readableLabel(part._relationshipLabel || relationship || ""),
        parts: [],
      };
      byKey.set(key, band);
      bands.push(band);
    }
    band.parts.push({ part, index });
  });
  return bands;
}

// How many of each band's parts are drawn when there are more than the cap.
// Every band keeps at least one - a band reduced to nothing would read as a
// relationship the object doesn't have - and the rest of the allowance is
// shared out in proportion, largest remainder first, so the same input always
// gives the same answer.
function share(sizes, cap) {
  const counts = sizes.map((n) => Math.max(0, Math.floor(n)));
  const total = counts.reduce((sum, n) => sum + n, 0);
  if (total <= cap) return counts;
  const room = Math.max(counts.length, cap);
  const exact = counts.map((n) => (n * room) / total);
  const taken = exact.map((n, i) => Math.min(counts[i], Math.max(1, Math.floor(n))));
  let left = room - taken.reduce((sum, n) => sum + n, 0);
  const order = exact
    .map((value, i) => ({ i, remainder: value - Math.floor(value) }))
    .sort((a, b) => b.remainder - a.remainder || a.i - b.i);
  let guard = 0;
  while (left > 0 && guard < counts.length * 4) {
    let moved = false;
    for (let n = 0; n < order.length && left > 0; n += 1) {
      const { i } = order[n];
      if (taken[i] >= counts[i]) continue;
      taken[i] += 1;
      left -= 1;
      moved = true;
    }
    if (!moved) break;
    guard += 1;
  }
  return taken;
}

// ---------------------------------------------------------------------------
// The drawing

const clamp = (value, low, high) => Math.min(high, Math.max(low, value));

// How many cards fit across, given the room there is. One on a phone, five or
// six on a desktop - never more, because a seventh column makes the text the
// smallest thing on screen again.
function columnsFor(width, metrics = METRICS) {
  return gridFor(width, metrics).columns;
}

// The grid one width gives: how many cards fit across, and how wide each one
// is once the leftover room is shared between them. A card never shrinks
// below its own width and never grows past a third again - a card as wide as
// a paragraph stops reading as a box - so the picture fills the screen it is
// on without turning into something else on a wider one.
function gridFor(width, metrics = METRICS) {
  const m = metrics;
  const room = Math.max(m.cardWidth, width - m.margin * 2 - m.railInset);
  const columns = clamp(Math.floor((room + m.gapX) / (m.cardWidth + m.gapX)), 1, m.maxColumns);
  const even = Math.floor((room - (columns - 1) * m.gapX) / columns);
  const cardWidth = clamp(even, m.cardWidth, Math.round(m.cardWidth * 1.35));
  return { columns, cardWidth, contentWidth: columns * cardWidth + (columns - 1) * m.gapX };
}

// A relationship's own name, read as words where nobody has renamed it.
// `SCENARIO_CLUSTER_OF` is what the graph calls it, not what a reader calls
// it; an admin who set a label in Views gets that label exactly, and a name
// with a space in it is already somebody's wording and is left alone.
function readableLabel(label) {
  const value = String(label || "").trim();
  if (!value || /\s/.test(value)) return value;
  if (!/^[A-Za-z][A-Za-z0-9_]*$/.test(value)) return value;
  const words = value
    .replace(/_+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .trim();
  return words ? words[0].toUpperCase() + words.slice(1) : value;
}

const cardHeight = (lines, hasFooter, m) =>
  m.cardPadding * 2 + m.typeLineHeight + lines * m.titleLineHeight + (hasFooter ? m.footerHeight : 0);

// The path from one card to the next, in reading order.
//
// Two cards in the same row are joined by a straight arrow through the gap
// between them. A card at the end of a row is joined to the first of the next
// by an orthogonal path that runs out into the gap, down through the space
// between the rows and back - so the line never crosses a card, whatever the
// cards contain. Exactly the way a flow drawn by hand wraps.
function connector(a, b, m) {
  const ay = a.y + a.height / 2;
  const by = b.y + b.height / 2;
  if (Math.abs(ay - by) < 1 && b.x > a.x) {
    return { d: `M ${a.x + a.width} ${ay} L ${b.x} ${by}`, kind: "next" };
  }
  const out = a.x + a.width + m.gapX / 2;
  const back = b.x - m.gapX / 2;
  const lane = (a.y + a.height + b.y) / 2;
  return {
    d: `M ${a.x + a.width} ${ay} L ${out} ${ay} L ${out} ${lane} L ${back} ${lane} L ${back} ${by} L ${b.x} ${by}`,
    kind: "next",
  };
}

// Lay out one object and its parts.
//
//   object  { guid, name, type, label, colour }
//   parts   the server's assignments, each with _relationship, _direction,
//           _relationshipLabel, and whatever the caller resolved for it
//           (label, colour, hasDiagram, opens)
//   width   the room on screen, in CSS pixels
//   ordered whether the order is the object's own flow (the server says);
//           only then are the parts joined by arrows, because only then does
//           one part actually follow another
//   cap     the most parts drawn
//
// Answers plain data: every box with its x, y, width, height and its wrapped
// lines, every connector as an SVG path, and the size of the whole picture.
function layout({ object, parts = [], width = 1024, ordered = false, cap = DEFAULT_CAP, metrics = METRICS } = {}) {
  const m = metrics;
  const { columns, cardWidth, contentWidth } = gridFor(width, m);
  const left = m.margin + m.railInset;
  const textWidthInCard = cardWidth - m.cardPadding * 2 - 8;

  // The object itself, as a title block across the top.
  const objectWidth = Math.min(m.objectMaxWidth, Math.max(cardWidth, contentWidth));
  const objectLines = wrapText(object?.name, objectWidth - m.cardPadding * 2 - 8, m.objectFontSize, m.objectLines);
  const titleBox = {
    kind: "object",
    guid: object?.guid || "",
    name: object?.name || "",
    typeLabel: object?.label || object?.type || "",
    colour: object?.colour || "#1a1a2e",
    lines: objectLines.length ? objectLines : [object?.name || "This object"],
    x: left,
    y: m.margin,
    width: objectWidth,
    height: m.cardPadding * 2 + m.typeLineHeight + Math.max(1, objectLines.length) * m.objectLineHeight,
  };

  const grouped = bandsOf(parts);
  const allowance = share(grouped.map((band) => band.parts.length), Math.max(1, cap));

  const bands = [];
  const edges = [];
  let y = titleBox.y + titleBox.height + m.bandGap;

  grouped.forEach((band, bandIndex) => {
    const shown = band.parts.slice(0, allowance[bandIndex]);
    const hidden = band.parts.length - shown.length;
    const heading = {
      label: band.label,
      direction: band.direction,
      count: band.parts.length,
      x: left,
      y,
      width: contentWidth,
      height: m.headingHeight,
    };
    // The rail from the object to this band: the object is what these parts
    // belong to, and the tick says so once per relationship rather than once
    // per part - forty lines into forty boxes is not a picture of anything.
    edges.push({
      kind: "belongs",
      d: `M ${titleBox.x + m.railInset / 2} ${titleBox.y + titleBox.height} L ${m.margin} ${titleBox.y + titleBox.height} L ${m.margin} ${y + m.headingHeight / 2} L ${left} ${y + m.headingHeight / 2}`,
    });

    let rowTop = y + m.headingHeight;
    const cards = [];
    const entries = shown.map(({ part, index }) => {
      const lines = wrapText(part.name, textWidthInCard, m.titleFontSize, m.titleLines);
      return { part, index, lines };
    });
    if (hidden > 0) entries.push({ more: hidden, lines: wrapText(`and ${hidden} more`, textWidthInCard, m.titleFontSize, 1) });

    for (let start = 0; start < entries.length; start += columns) {
      const row = entries.slice(start, start + columns);
      const height = Math.max(
        ...row.map((entry) => cardHeight(Math.max(1, entry.lines.length), !entry.more && Boolean(entry.part?.opens), m))
      );
      const top = rowTop;
      row.forEach((entry, column) => {
        cards.push({
          kind: entry.more ? "more" : "part",
          more: entry.more || 0,
          guid: entry.part?.GUID || "",
          name: entry.part?.name || "",
          typeLabel: entry.part?.label || entry.part?.Type || "",
          colour: entry.part?.colour || "#8a90a2",
          opens: Boolean(entry.part?.opens),
          hasDiagram: Boolean(entry.part?.hasDiagram),
          step: typeof entry.index === "number" ? entry.index + 1 : 0,
          lines: entry.lines.length ? entry.lines : [entry.part?.name || ""],
          x: left + column * (cardWidth + m.gapX),
          y: top,
          width: cardWidth,
          height,
        });
      });
      rowTop += height + m.gapY;
    }

    // One part follows another only where the order is the object's own flow.
    // Anywhere else the order is the graph's, and an arrow would claim a
    // sequence the library never recorded.
    if (ordered) {
      const drawn = cards.slice(cards.length - entries.length);
      for (let i = 0; i + 1 < drawn.length; i += 1) {
        if (drawn[i].kind !== "part" || drawn[i + 1].kind !== "part") continue;
        // An arrow says "the flow runs this one straight after that one", so
        // it is only drawn where that is exactly true. Two parts of the same
        // relationship with another relationship's part between them in the
        // flow are neighbours on the page, not in the process, and get no
        // arrow - the picture never claims an order the library never had.
        if (drawn[i + 1].step !== drawn[i].step + 1) continue;
        edges.push(connector(drawn[i], drawn[i + 1], m));
      }
    }

    // The heading's rule runs as far as the band's own cards, never to the
    // full width of the grid: a section of three cards underlined across an
    // empty half of the screen reads as something cut off.
    const reach = cards.length ? Math.max(...cards.map((card) => card.x + card.width)) - heading.x : contentWidth;
    bands.push({
      ...heading,
      width: Math.max(m.cardWidth, Math.min(contentWidth, reach)),
      relationship: band.relationship,
      shown: cards.slice(cards.length - entries.length),
      hidden,
    });
    y = rowTop - m.gapY + m.bandGap;
  });

  const cards = bands.flatMap((band) => band.shown);
  const bottom = cards.length ? Math.max(...cards.map((card) => card.y + card.height)) : titleBox.y + titleBox.height;
  return {
    width: left + contentWidth + m.margin,
    height: bottom + m.margin + m.legendSpace,
    columns,
    title: titleBox,
    bands,
    cards,
    edges,
    ordered: Boolean(ordered) && cards.some((card) => card.kind === "part"),
    shown: cards.filter((card) => card.kind === "part").length,
    total: (parts || []).length,
  };
}


// ---------------------------------------------------------------------------
// How it is painted
//
// Geometry alone is not enough to draw the same picture twice: the ink, the
// text sizes and the offsets inside a box have to agree as well, or the
// browser's drawing and the file the server writes drift apart a line at a
// time. They live here, with the geometry, and both renderers read them.

// A readable ink for text sitting on a tint of the type's own colour. The
// tints are light by construction (8-10%), so the brand ink always wins; this
// is here so a very dark configured colour can't make its own chip unreadable.
const PAINT = Object.freeze({
  page: "#ffffff",
  ink: "#1a1a2e",
  rail: "#c3c8d4",
  arrow: "#5b6278",
  heading: "#6b7280",
  headingCount: "#9aa0ab",
  rule: "#e7e9ef",
  moreFill: "#fbfbfd",
  moreStroke: "#c3c8d4",
  moreInk: "#6b7280",
  badge: "#1a1a2e",
  badgeMark: "#f0e631",
  legend: "#6b7280",
});

// Where the words sit inside a box, measured from its own top-left corner.
const TEXT = Object.freeze({
  object: { x: 16, typeY: 22, typeSize: 10, firstLine: 43, lineHeight: 22, size: 17 },
  card: { x: 20, typeY: 21, typeSize: 9.5, firstLine: 38, lineHeight: 16, size: 13 },
  band: { textY: 14, size: 10.5, ruleY: 20 },
});

// The mark on a card that opens something - a circle with an arrow out of it,
// the same one the Flow puts on a step that has a diagram of its own.
const BADGE = Object.freeze({ inset: 14, radius: 8.5 });

const badgeCentre = (card) => ({ cx: card.x + card.width - BADGE.inset, cy: card.y + BADGE.inset });

const badgeArrow = (card) => {
  const { cx, cy } = badgeCentre(card);
  return `M ${cx - 4} ${cy + 4} L ${cx + 4} ${cy - 4} M ${cx - 2} ${cy - 4} L ${cx + 4} ${cy - 4} L ${cx + 4} ${cy + 2}`;
};

// A wash of a type's own colour. A colour written in any other notation simply
// washes in the brand's ink, so a picture is never left without a fill.
function tint(colour, alpha) {
  const match = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(colour || "").trim());
  if (!match) return `rgba(26, 26, 46, ${alpha})`;
  const hex = match[1].length === 3 ? match[1].replace(/./g, (c) => c + c) : match[1];
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// What a band's heading says after its name: how many parts are in it and,
// for an inbound relationship, which way it runs. Plain words - a reader
// shouldn't have to know what an arrow means in a graph.
// The gap before the count is three non-breaking spaces on purpose: SVG
// collapses ordinary leading spaces in a tspan, and the heading would read
// "PROCESS OF2 parts".
const bandNote = (band) =>
  `   ${band.count} ${band.count === 1 ? "part" : "parts"}${band.direction === "in" ? ", pointing here" : ""}`;

// The one line under the picture: how much of the object was drawn, and
// whether the order is the object's own flow. `flowName` is what this database
// calls a BPMN ("Flow" unless an admin renamed it).
const legendText = (drawing, { total, truncated = false, flowName = "flow" } = {}) => {
  const all = total || drawing.total;
  return (
    `${drawing.shown} of ${all} ${all === 1 ? "part" : "parts"} drawn` +
    (drawing.ordered ? ` · in the order of the ${flowName}` : "") +
    (truncated ? " · more in the library" : "")
  );
};

module.exports = {
  METRICS,
  DEFAULT_CAP,
  PAINT,
  TEXT,
  BADGE,
  badgeCentre,
  badgeArrow,
  tint,
  bandNote,
  legendText,
  textWidth,
  wrapText,
  bandsOf,
  share,
  columnsFor,
  gridFor,
  readableLabel,
  layout,
};
